import {
  endOfDay,
  isThisISOWeek,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
  startOfDay,
} from 'date-fns';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsVisible } from 'react-is-visible';

import { isNil } from 'lodash-es';
import { useDispatch } from 'react-redux';
import useDashboardNewAppointmentModal from '../../../hooks/useDashboardNewAppointmentModal';
import useTimezone from '../../../hooks/useTimezone';
import type {
  GetAppointmentDayDetailsResponseAppointmentType,
  GetAppointmentDayDetailsResponseItemType,
} from '../../../redux/ducks/DashboardDucks.types';
import { setDashboardNewAppointmentModalInputTime } from '../../../redux/ducks/UIDucks';
import Spinner from '../Spinner/Spinner';
import CalendarDayOverviewDayItem from './CalendarDayOverviewDayItem';

type CalendarDayOverviewDayBlockProps = {
  key: string;
  index: number;
  data: GetAppointmentDayDetailsResponseItemType;
  previousDayData?: GetAppointmentDayDetailsResponseItemType;
  loading: boolean;
  rootMargin: string;
  onThreshold?: (data: GetAppointmentDayDetailsResponseItemType) => void;
  startHour: number;
  endHour: number;
};

const DAY_DETAILS_BLOCK_SIZE = 960;

const CalendarDayOverviewDayBlock: React.FC<
  CalendarDayOverviewDayBlockProps
> = ({
  key,
  data,
  loading,
  rootMargin,
  onThreshold,
  startHour,
  endHour,
  previousDayData,
}) => {
  const dayBlockHeight: number =
    Math.round(DAY_DETAILS_BLOCK_SIZE / 12) * (endHour - startHour);
  const yPerSec: number =
    0.0 +
    dayBlockHeight / (endHour * 60 * 60 * 1000 - startHour * 60 * 60 * 1000);

  let observer: IntersectionObserver | undefined;
  const { t, i18n } = useTranslation();
  const nodeRef = useRef<HTMLDivElement>(null);
  const todayRef = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(nodeRef);
  const newAppointmentModal = useDashboardNewAppointmentModal();
  const dispatch = useDispatch();
  const { currentTimezone } = useTimezone();
  const todayPointerOffset = useMemo<string | undefined>(() => {
    if (isToday(data.timestamp)) {
      const dayStart = startOfDay(data.timestamp).getTime();
      const offsetSecs = new Date().getTime() - dayStart;
      return `${Math.round(offsetSecs * yPerSec)}px`;
    }

    return undefined;
  }, [startHour]);

  const formatDateChangeLabel = useCallback((ts: number, short: boolean) => {
    const messageDate = new Date(ts);

    if (isToday(messageDate)) {
      return t('static.generic.today');
    }
    if (isYesterday(messageDate)) {
      return t('static.generic.yesterday');
    }
    if (isTomorrow(messageDate)) {
      return t('static.generic.tomorrow');
    }
    if (isThisISOWeek(messageDate)) {
      return t('static.generic.this_week_day', {
        name: Intl.DateTimeFormat(i18n.language, {
          weekday: 'long',
          timeZone: currentTimezone,
        }).format(messageDate),
      });
    }
    if (isThisYear(messageDate)) {
      return Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    }
    if (short) {
      return Intl.DateTimeFormat(i18n.language, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: currentTimezone,
      }).format(new Date(messageDate));
    }

    return Intl.DateTimeFormat(i18n.language, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: currentTimezone,
    }).format(new Date(messageDate));
  }, []);

  const elementHasIntersected = (
    entries: IntersectionObserverEntry[],
    o: any,
  ) => {
    for (let idx = 0; idx < entries.length; idx++) {
      const entry = entries[idx];
      if (entry.isIntersecting && onThreshold) {
        onThreshold(data);
      }
    }
  };

  useEffect(() => {
    if (nodeRef?.current) {
      if (isNil(observer)) {
        const ioConfiguration = {
          rootMargin,
          threshold: 0,
        };

        observer = new IntersectionObserver(
          elementHasIntersected,
          ioConfiguration,
        );
      }

      if (isVisible) {
        observer.observe(nodeRef.current);
      } else {
        observer.unobserve(nodeRef.current);
      }
    }
  }, [isVisible, nodeRef]);

  const sortedAppointments = useMemo<
    GetAppointmentDayDetailsResponseAppointmentType[]
  >(() => {
    const startOfIntervalDay = startOfDay(new Date(data.timestamp));

    const allAppointments = !isNil(previousDayData)
      ? [...data.appointments, ...previousDayData.appointments]
      : [...data.appointments];

    return [...allAppointments]
      .filter((a) => a.to > startOfIntervalDay.getTime())
      .sort((a, b) => a.from - b.from);
  }, [data, previousDayData]);

  const dayBlockHours = useMemo<string[]>(() => {
    const intervalsAmount = endHour - startHour;

    const result: string[] = [];
    for (let index = 0; index < intervalsAmount; index++) {
      const start = startOfDay(new Date());
      const marker = new Date(
        start.getTime() + (startHour + index) * 60 * 60 * 1000,
      );
      result.push(
        Intl.DateTimeFormat(i18n.language, {
          hour: '2-digit',
          minute: 'numeric',
          hour12: false,
          timeZone: currentTimezone,
        }).format(marker),
      );
    }

    return result;
  }, []);

  const minDayBlockH = `min-h-[${dayBlockHeight}px] max-h-[${dayBlockHeight}px]`;
  const minDayBlockHStyle = `${dayBlockHeight}px`;

  return loading ? (
    <div
      id={key}
      className="my-1 flex min-w-full snap-center flex-col items-center justify-center rounded-lg bg-white py-10"
    >
      <span className="grow">
        <Spinner color="fill-slate-200" />
      </span>
    </div>
  ) : (
    <div
      id={key}
      className="relative m-0 flex min-w-full snap-center flex-col p-0"
      ref={nodeRef}
    >
      <div className="sticky top-0 flex min-h-[32px] min-w-full flex-col items-center justify-center">
        <div className="flex min-h-full min-w-full items-center pt-5">
          <div className="min-h-full flex-1 border-t border-dashed border-slate-300">
            &nbsp;
          </div>
          <span className="mt-[-20px] flex min-h-full flex-col items-start justify-start bg-white px-3 text-sm font-bold capitalize text-slate-500">
            {formatDateChangeLabel(data.timestamp, false)}
          </span>
          <div className="min-h-full flex-1 border-t border-dashed border-slate-300">
            &nbsp;
          </div>
        </div>
      </div>

      <div
        className={`relative flex ${minDayBlockH} grow flex-col gap-2 pb-10`}
        style={{ height: minDayBlockHStyle }}
      >
        <button
          type="button"
          className={`group absolute top-0 m-0 flex size-full ${minDayBlockH} flex-row items-start justify-start border border-white bg-slate-50 p-0 hover:border-slate-100`}
          data-test-id="day-block-all-btn"
          id="day-block-all-btn"
          key="day-block-all-btn"
          aria-label="day-block-all-btn"
          onClick={() => {
            dispatch(setDashboardNewAppointmentModalInputTime(data.timestamp));
            newAppointmentModal.onOpen();
          }}
        >
          <span className="absolute top-0 m-0 grid min-h-full min-w-full grid-cols-1 gap-0 p-0">
            {dayBlockHours.map((dayBlockHour, index) => (
              <span
                key={`day-block-hour-${data.timestamp}`}
                className="flex size-full flex-col items-start justify-start border-t border-dotted border-slate-300 pl-1 text-start text-xs font-semibold text-slate-500"
              >
                <div className="flex max-w-[64px] flex-col">
                  <span>{dayBlockHour}</span>
                  {index % 4 === 0 && (
                    <span className="capitalize">
                      {formatDateChangeLabel(data.timestamp, true)}
                    </span>
                  )}
                </div>
              </span>
            ))}
          </span>
          <span className="m-0 flex size-full grow flex-row items-start justify-start p-0">
            {sortedAppointments.map((item, index) => {
              const startOfIntervalDay = startOfDay(new Date(data.timestamp));
              const endOfIntervalDay = endOfDay(new Date(data.timestamp));

              const yOffset: number = Math.round(
                (Math.max(item.from - startOfIntervalDay.getTime(), 0) -
                  startHour * 60 * 60 * 1000) *
                  yPerSec,
              );

              const height: number = Math.round(
                (Math.min(endOfIntervalDay.getTime(), item.to) -
                  Math.max(item.from, startOfIntervalDay.getTime())) *
                  yPerSec,
              );

              return (
                <CalendarDayOverviewDayItem
                  data={item}
                  index={index}
                  yOffset={yOffset}
                  height={height}
                />
              );
            })}
          </span>
          {todayPointerOffset && (
            <span
              className="absolute z-20 min-w-full border-t-2 border-sky-600 text-xs"
              style={{ top: todayPointerOffset }}
              ref={todayRef}
            >
              &nbsp;
            </span>
          )}
        </button>
      </div>
    </div>
  );
};

export default CalendarDayOverviewDayBlock;
